/**
 * Guide engine v3 overlay - top-level (#1025, epic #1020).
 *
 * The thin adapter between the headless runtime (#1024) and the visual layer. It
 * reads the runtime context and renders, into a body portal so fixed positioning
 * escapes any transformed ancestor:
 *   - the rings + popup while a step is `shown` (after the highlight-wait, so no
 *     half-ringed step, R11);
 *   - the error chip while the runtime is in `error`;
 *   - nothing while idle / waiting / completed / exited.
 *
 * No spotlight layer: the page stays visible behind the popup (R15). On a gated
 * step, off-target controls are dimmed by runtime/gating.js, not by the overlay.
 * Keyed by stepId so the popup + ring reveal restart each step.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@mui/material/styles';
import { useGuideRuntime } from '../runtime/GuideRuntimeContext.jsx';
import GuideRings from './GuideRings.jsx';
import GuidePopup from './GuidePopup.jsx';
import GuideErrorChip from './GuideErrorChip.jsx';
import { DEFAULT_GUIDE_STRINGS } from './strings';

const GuideOverlay = ({ strings = DEFAULT_GUIDE_STRINGS, ringStaggerMs, onReplay = null }) => {
  const theme = useTheme();
  const rt = useGuideRuntime();
  const [container] = useState(() => (typeof document !== 'undefined' ? document.body : null));
  if (!container) return null;

  // Rings AND popup sit at exactly the modal z-index (#1044, two rounds):
  // same-z portals stack by DOM order, so both paint above the already-open
  // dialog they mount after (highlighting + narrating controls inside modals),
  // while a dropdown/popover the user opens NEXT mounts later and paints above
  // both - its menu items stay visible and clickable even when the menu grows
  // into the popup's authored region (round 2: Pending Income was unreachable
  // under the card). A menu covering part of the card mid-selection is fine:
  // the user already read the step. Only the error chip stays above everything
  // (small corner element, never in a menu's path).
  const guideZ = theme.zIndex.modal;

  let content = null;
  if (rt.isError) {
    content = (
      <GuideErrorChip reason={rt.errorReason} onExit={rt.exit} strings={strings} zIndex={guideZ + 102} />
    );
  } else if (rt.isShown && rt.step) {
    content = (
      <>
        <GuideRings
          key={`rings-${rt.stepId}`}
          highlights={rt.highlights}
          staggerMs={ringStaggerMs}
          zIndex={guideZ}
        />
        <GuidePopup
          key={`popup-${rt.stepId}`}
          step={rt.step}
          manualButtons={rt.manualButtons}
          actionHint={rt.actionHint}
          onExit={rt.exit}
          onManual={rt.selectManual}
          onReplay={onReplay}
          strings={strings}
          zIndex={guideZ}
        />
      </>
    );
  }

  if (!content) return null;
  return createPortal(content, container);
};

export default GuideOverlay;
